/* eslint-disable complexity */
/**
 * RMC Order Request Service
 *
 * Handles API calls for submitting RMC order requests via the v2/orderrequests endpoint.
 * Order requests are processed asynchronously by the backend - they don't create orders
 * immediately but submit them for processing.
 *
 * This service handles:
 * - API calls using hub's api client
 * - Analytics tracking (product-specific)
 * - Order persistence callbacks (UI-specific)
 *
 * Business logic (mapping form data to API payload) is in @hconnect/tuc-processes/orderIntake
 */

import {trackEvent} from '@hconnect/common/logging/Analytics'
import {
  mapOrderFormToOrderRequest,
  OrderRequestPayload,
  OrderRequestResponse
} from '@hconnect/tuc-processes/orderIntake'

import {api} from '../../../../App.global'
import {V2_ORDER_REQUESTS_API_PATH} from '../../../../Organisms/OrderIntake/OrderIntake.action'
import {RmcOrder, RmcOrderPlacementResult} from '../types/order'
import {OrderIntakeSite} from '../types/OrderIntakeSite.types'

/**
 * Extended payload for UI persistence - includes material descriptions.
 * This is hub-specific and used for displaying previous orders in the UI.
 *
 * Extends OrderRequestPayload from tuc-processes with UI-specific fields.
 */
export interface RmcOrderPayload extends OrderRequestPayload {
  materialId: string
  materialDescription: string
  customerMaterialDescription?: string
}

/**
 * Places RMC orders via the v2/orderrequests API endpoint
 * Makes individual API calls for each order using RMC-specific payload structure
 * @param orders Array of RMC orders to place
 * @param jobSite The selected job site containing siteId and other site information
 * @param pumpSettings Optional pump settings containing availablePumpSizes with leadTime information
 * @param regionFeatures Region-specific feature flags to control payload structure
 * @param onSuccessfulPlacement Optional callback to handle successful placements (e.g., for persistence)
 * @param apiPath Optional API base path (defaults to v2/OrderRequests)
 * @returns Promise with placement result
 */
export const placeRmcOrders = async (
  orders: RmcOrder[],
  jobSite: OrderIntakeSite,
  onSuccessfulPlacement?: (payloads: RmcOrderPayload[]) => void,
  apiPath?: string
): Promise<RmcOrderPlacementResult> => {
  const startTime = Date.now()
  const placedOrderIds: string[] = []
  const failedOrderIds: string[] = []
  const errors: Record<string, string> = {}
  const requestIds: Record<string, string> = {} // Map orderId to requestId
  const successfulPayloads: RmcOrderPayload[] = []

  try {
    // Process each order individually
    for (const order of orders) {
      let apiPayload: OrderRequestPayload | undefined

      try {
        // Use shared mapping from tuc-processes to build order request payload
        apiPayload = mapOrderFormToOrderRequest(order.formData, {
          site: {
            country: jobSite.country,
            orgUnitId: jobSite.orgUnitId,
            customerId: jobSite.customerId,
            siteId: jobSite.siteId
          }
        })

        // Track individual order placement attempt
        trackEvent('rmcIndividualOrderPlacementAttempt', {
          businessLine: 'RMC',
          payload: apiPayload,
          order: {
            id: order.id,
            contractNumber: order.formData.contract?.contractNumber
          }
        })

        // Make individual API call for this order using v2/orderrequests endpoint
        const basePath = apiPath ?? V2_ORDER_REQUESTS_API_PATH
        const response = await api.post<OrderRequestResponse>(basePath, apiPayload)

        // Check if the response indicates success
        if (response.status >= 200 && response.status < 300) {
          placedOrderIds.push(order.id)

          // Capture requestId from response for status checking
          if (response.data.requestId) {
            requestIds[order.id] = response.data.requestId
          }

          // Create enriched payload for persistence (includes orderedMaterialDescription)
          // TODO: check if needed?
          const persistencePayload: RmcOrderPayload = {
            ...apiPayload,
            materialId: order.formData.material?.materialId || '',
            materialDescription: order.formData.material?.materialDescription || '',
            customerMaterialDescription:
              order.formData.material?.customerMaterialDescription || undefined
          }
          successfulPayloads.push(persistencePayload) // Store enriched payload

          // Track successful individual order placement
          trackEvent('rmcIndividualOrderPlacementSuccess', {
            businessLine: 'RMC',
            payload: apiPayload,
            order: {
              id: order.id,
              contractNumber: order.formData.contract?.contractNumber
            }
          })
        } else {
          failedOrderIds.push(order.id)
          errors[order.id] = `HTTP ${response.status}: Request failed`

          // Track failed individual order placement due to HTTP error
          trackEvent('rmcIndividualOrderPlacementHttpError', {
            businessLine: 'RMC',
            responseStatus: response.status,
            error: `HTTP ${response.status}: Request failed`,
            payload: apiPayload,
            order: {
              id: order.id,
              contractNumber: order.formData.contract?.contractNumber
            }
          })
        }
      } catch (orderError) {
        // Handle individual order failure
        failedOrderIds.push(order.id)

        let errorMessage = 'Unknown error occurred'

        // Extract error message from order placement API response
        if (orderError && typeof orderError === 'object') {
          const axiosError = orderError as {
            response?: {
              status?: number
              data?: {
                detail?: string
                title?: string
              }
            }
            message?: string
          }

          // Order placement API always returns errors in this format:
          // { type: "...", title: "...", status: number, detail: "..." }
          if (axiosError.response?.data?.detail) {
            errorMessage = axiosError.response.data.detail
          } else if (axiosError.response?.data?.title) {
            errorMessage = axiosError.response.data.title
          } else if (axiosError.message) {
            errorMessage = axiosError.message
          }
        } else if (orderError instanceof Error) {
          errorMessage = orderError.message
        }

        errors[order.id] = errorMessage

        // Track individual order placement error
        trackEvent('rmcIndividualOrderPlacementError', {
          businessLine: 'RMC',
          orderId: order.id,
          error: errorMessage,
          httpStatus: (orderError as {response?: {status?: number}})?.response?.status,
          payload: apiPayload,
          order: {
            id: order.id,
            materialId: order.formData.material?.materialNumber
          }
        })
      }
    }

    // Determine overall success
    const success = placedOrderIds.length > 0 && failedOrderIds.length === 0

    // Call the callback with successful payloads if provided
    if (onSuccessfulPlacement && successfulPayloads.length > 0) {
      onSuccessfulPlacement(successfulPayloads)
    }

    // Track overall RMC order placement batch completion
    trackEvent('rmcOrderPlacementBatchCompleted', {
      businessLine: 'RMC',
      totalOrders: orders.length,
      successfulOrders: placedOrderIds.length,
      failedOrders: failedOrderIds.length,
      successRate: (placedOrderIds.length / orders.length) * 100,
      success: success,
      placedOrderIds,
      failedOrderIds,
      duration: Date.now() - startTime
    })

    return {
      success,
      placedOrderIds,
      failedOrderIds,
      errors: failedOrderIds.length > 0 ? errors : {},
      requestIds
    }
  } catch (error) {
    // Handle complete failure (shouldn't happen with individual processing)
    console.error('Error placing RMC orders:', error)

    const errorMessage = error instanceof Error ? error.message : 'Failed to place orders'
    const orderIds = orders.map((order) => order.id)

    // Track complete batch failure
    trackEvent('rmcOrderPlacementBatchFailed', {
      businessLine: 'RMC',
      totalOrders: orders.length,
      failedOrders: orders.length,
      successRate: 0,
      error: errorMessage,
      orderIds,
      duration: Date.now() - startTime
    })

    return {
      success: false,
      placedOrderIds: [],
      failedOrderIds: orderIds,
      errors: {
        general: errorMessage
      },
      requestIds: {}
    }
  }
}
